import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/admin/reports")({
  head: () => ({ meta: [{ title: "User Profit / Loss Report — Admin" }] }),
  component: ReportsPage,
});

const fmt = (n: number) => `₹${Math.abs(n).toLocaleString("en-IN")}`;
const signed = (n: number) => (n > 0 ? `+${fmt(n)}` : n < 0 ? `-${fmt(n)}` : `₹0`);

function plLabel(n: number) {
  if (n > 0) return { text: `Company Profit: +${fmt(n)}`, cls: "text-success" };
  if (n < 0) return { text: `Company Loss: -${fmt(n)}`, cls: "text-destructive" };
  return { text: `No Profit / No Loss: ₹0`, cls: "text-muted-foreground" };
}

function ReportsPage() {
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const { data: profiles } = useQuery({
    queryKey: ["report_profiles"],
    queryFn: async () =>
      (await supabase.from("profiles").select("id, name, phone, wallet_balance, winning_balance")).data ?? [],
  });

  const { data: txns } = useQuery({
    queryKey: ["report_txns", from, to],
    queryFn: async () => {
      let query = supabase
        .from("wallet_transactions")
        .select("user_id, type, amount, status, created_at, meta")
        .eq("status", "success")
        .in("type", ["add", "admin_credit", "withdraw"])
        .limit(50000);
      if (from) query = query.gte("created_at", from);
      if (to) query = query.lte("created_at", to + "T23:59:59");
      return (await query).data ?? [];
    },
  });

  // A transaction counts as "deposit received" only when it is real money in:
  //  - legacy approved deposit requests (type "add") that actually credited the wallet
  //  - manual wallet credits made by admin / sub-admin (type "admin_credit" via admin_adjust_wallet)
  // Reversed credits, uncredited auto-gateway attempts, wins, refunds, late bet
  // refunds and win reversals are never counted.
  const isDeposit = (t: any) => {
    const m = (t.meta ?? {}) as any;
    if (m.reversed_at) return false;
    if (t.type === "add") return m.source !== "auto" || m.credited === true;
    if (t.type === "admin_credit") return !m.source || m.source === "admin_adjust";
    return false;
  };


  const rows = useMemo(() => {
    const map = new Map<string, any>();
    for (const p of profiles ?? []) {
      const main = Number(p.wallet_balance ?? 0);
      const withdrawable = Number(p.winning_balance ?? 0);
      map.set(p.id, {
        id: p.id,
        name: p.name,
        phone: p.phone,
        main,
        withdrawable,
        total: main + withdrawable,
        deposit: 0,
        withdraw: 0,
      });
    }
    // Guard against double-counting the same payment when an approved deposit
    // request also produced a manual wallet credit (same user + UTR/reference).
    const seen = new Set<string>();
    for (const t of txns ?? []) {
      const r = map.get(t.user_id);
      if (!r) continue;
      const a = Number(t.amount);
      if (t.type === "withdraw") {
        r.withdraw += a;
        continue;
      }
      if (!isDeposit(t)) continue;
      const m = (t.meta ?? {}) as any;
      const ref = m.utr ?? m.reference ?? m.deposit_request_id ?? null;
      if (ref) {
        const key = `${t.user_id}:${ref}:${a}`;
        if (seen.has(key)) continue;
        seen.add(key);
      }
      r.deposit += a;
    }

    let arr = Array.from(map.values()).map((r) => ({
      ...r,
      pl: r.deposit - r.withdraw - r.total, // + = company profit, - = company loss
    }));
    if (q) {
      const s = q.toLowerCase();
      arr = arr.filter(
        (r) => (r.name ?? "").toLowerCase().includes(s) || (r.phone ?? "").toLowerCase().includes(s),
      );
    }
    arr = arr.filter((r) => r.deposit || r.withdraw || r.total);
    arr.sort((a, b) => b.pl - a.pl);
    return arr;
  }, [profiles, txns, q]);

  const totals = rows.reduce(
    (s, r) => ({
      deposit: s.deposit + r.deposit,
      withdraw: s.withdraw + r.withdraw,
      total: s.total + r.total,
      pl: s.pl + r.pl,
    }),
    { deposit: 0, withdraw: 0, total: 0, pl: 0 },
  );

  const totalsPL = plLabel(totals.pl);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-black">User Profit / Loss Report</h1>
        <p className="text-xs text-muted-foreground">
          Company P/L = Total Deposits Received − Total Paid Withdrawals − Current Total Wallet Balance (Main + Withdrawable).
          Positive = Company Profit / User Loss. Negative = Company Loss / User Profit.
        </p>

      </div>

      <div className="grid gap-2 rounded-2xl border bg-card p-3 md:grid-cols-3">
        <Input placeholder="Search name / mobile" value={q} onChange={(e) => setQ(e.target.value)} />
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {[
          ["Total Deposits Received", signed(totals.deposit)],
          ["Total Paid Withdrawals", signed(totals.withdraw)],

          ["Current Total Balance", signed(totals.total)],
        ].map(([label, val]) => (
          <div key={label as string} className="rounded-xl border bg-card p-3">
            <p className="text-[10px] uppercase text-muted-foreground">{label as string}</p>
            <p className="text-sm font-black">{val as string}</p>
          </div>
        ))}
        <div className="rounded-xl border bg-card p-3">
          <p className="text-[10px] uppercase text-muted-foreground">Company P/L</p>
          <p className={`text-sm font-black ${totalsPL.cls}`}>{totalsPL.text}</p>
        </div>
      </div>

      <div className="-mx-4 overflow-x-auto rounded-xl border bg-card sm:mx-0">
        <table data-sticky-col className="w-full min-w-[900px] text-xs whitespace-nowrap">
          <thead className="bg-muted/40 text-left uppercase text-[10px] text-muted-foreground">
            <tr>
              <th className="p-2">User Name</th>
              <th className="p-2">Mobile</th>
              <th className="p-2 text-right">Total Deposit Received</th>
              <th className="p-2 text-right">Total Withdrawal Paid</th>
              <th className="p-2 text-right">Main Balance</th>
              <th className="p-2 text-right">Withdrawable Balance</th>
              <th className="p-2 text-right">Current Total Balance</th>
              <th className="p-2 text-right">Company Profit / Loss</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const pl = plLabel(r.pl);
              return (
                <tr key={r.id} className="border-t">
                  <td className="p-2 font-semibold">{r.name ?? "—"}</td>
                  <td className="p-2 text-muted-foreground">{r.phone ?? ""}</td>
                  <td className="p-2 text-right">{signed(r.deposit)}</td>
                  <td className="p-2 text-right">{signed(r.withdraw)}</td>
                  <td className="p-2 text-right">{signed(r.main)}</td>
                  <td className="p-2 text-right">{signed(r.withdrawable)}</td>
                  <td className="p-2 text-right font-semibold">{signed(r.total)}</td>
                  <td className={`p-2 text-right font-black ${pl.cls}`}>{pl.text}</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-muted-foreground">
                  No data.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
